/**
 * Extracted Lead Dialogs - Isolated state to prevent parent re-renders
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import ActivityFeedTimeline from '@/components/ActivityFeedTimeline';
import { toast } from 'sonner';
import { leadsAPI } from '@/lib/api';
import {
  UserPlus, Plus, Upload, Calendar, Clock, Eye, Loader2, StickyNote, DollarSign, CheckCircle2, Trash2
} from 'lucide-react';

const COUNTRIES = [
  'United Kingdom', 'Ireland', 'United States', 'Canada', 'Australia',
  'New Zealand', 'Germany', 'France', 'Netherlands', 'Sweden',
  'Denmark', 'Finland', 'Norway', 'Switzerland', 'Italy', 'Spain',
  'Austria', 'Belgium', 'Poland', 'Czech Republic', 'Hungary',
  'Portugal', 'Greece', 'Cyprus', 'Malta', 'UAE', 'Saudi Arabia',
  'Malaysia', 'Singapore', 'Japan', 'South Korea', 'China',
  'Turkey', 'Other'
];

const LEAD_SOURCES = [
  { value: 'manual', label: 'Manual Entry' },
  { value: 'website', label: 'Website Form' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'social_media', label: 'Social Media' },
  { value: 'referral', label: 'Referral' },
  { value: 'walk_in', label: 'Walk-in' },
  { value: 'phone_call', label: 'Phone Call' },
  { value: 'exhibition', label: 'Exhibition/Event' },
  { value: 'other', label: 'Other' },
];

/* ─── Add Lead Dialog ─── */
export const AddLeadDialog = ({ open, onOpenChange, onSuccess }) => {
  const [form, setForm] = useState({
    full_name: '', email: '', phone: '', city: '', country_interest: '',
    source: 'manual', source_campaign: '', notes: '',
    preferred_intake: '', education_level: '', budget_range: ''
  });
  const [saving, setSaving] = useState(false);

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async () => {
    if (!form.full_name || !form.phone) {
      toast.error('Name and phone are required');
      return;
    }
    setSaving(true);
    try {
      await leadsAPI.create(form);
      toast.success('Lead created successfully');
      onOpenChange(false);
      setForm({
        full_name: '', email: '', phone: '', city: '', country_interest: '',
        source: 'manual', source_campaign: '', notes: '',
        preferred_intake: '', education_level: '', budget_range: ''
      });
      onSuccess?.();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to create lead');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-teal-600" />
            Add New Lead
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Full Name *</Label>
              <Input value={form.full_name} onChange={(e) => update('full_name', e.target.value)} placeholder="Student Name" data-testid="add-lead-name" />
            </div>
            <div>
              <Label>Phone *</Label>
              <Input value={form.phone} onChange={(e) => update('phone', e.target.value)} placeholder="+92 300 1234567" data-testid="add-lead-phone" />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => update('email', e.target.value)} placeholder="email@example.com" data-testid="add-lead-email" />
            </div>
            <div>
              <Label>City</Label>
              <Input value={form.city} onChange={(e) => update('city', e.target.value)} placeholder="e.g. Peshawar" data-testid="add-lead-city" />
            </div>
            <div>
              <Label>Country Interest</Label>
              <Select value={form.country_interest} onValueChange={(v) => update('country_interest', v)}>
                <SelectTrigger><SelectValue placeholder="Select country" /></SelectTrigger>
                <SelectContent>
                  {COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Source</Label>
              <Select value={form.source} onValueChange={(v) => update('source', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LEAD_SOURCES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Campaign/Ref</Label>
              <Input value={form.source_campaign} onChange={(e) => update('source_campaign', e.target.value)} placeholder="Campaign ID or referrer" />
            </div>
            <div>
              <Label>Preferred Intake *</Label>
              <Select value={form.preferred_intake} onValueChange={(v) => update('preferred_intake', v)}>
                <SelectTrigger data-testid="add-lead-intake"><SelectValue placeholder="Select intake" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="September 2026">September 2026</SelectItem>
                  <SelectItem value="January 2027">January 2027</SelectItem>
                  <SelectItem value="May 2027">May 2027</SelectItem>
                  <SelectItem value="September 2027">September 2027</SelectItem>
                  <SelectItem value="January 2028">January 2028</SelectItem>
                  <SelectItem value="Undecided">Undecided</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Education Level</Label>
              <Select value={form.education_level} onValueChange={(v) => update('education_level', v)}>
                <SelectTrigger data-testid="add-lead-education"><SelectValue placeholder="Select level" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Foundation">Foundation</SelectItem>
                  <SelectItem value="Undergraduate">Undergraduate (Bachelors)</SelectItem>
                  <SelectItem value="Postgraduate">Postgraduate (Masters)</SelectItem>
                  <SelectItem value="PhD">PhD / Doctorate</SelectItem>
                  <SelectItem value="Diploma">Diploma / Certificate</SelectItem>
                  <SelectItem value="English Prep">English Prep</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Budget Range</Label>
              <Select value={form.budget_range} onValueChange={(v) => update('budget_range', v)}>
                <SelectTrigger data-testid="add-lead-budget"><SelectValue placeholder="Select budget" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Under £10k">Under £10k</SelectItem>
                  <SelectItem value="£10k-£20k">£10k – £20k</SelectItem>
                  <SelectItem value="£20k-£30k">£20k – £30k</SelectItem>
                  <SelectItem value="£30k+">£30k+</SelectItem>
                  <SelectItem value="Scholarship-dependent">Scholarship-dependent</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea value={form.notes} onChange={(e) => update('notes', e.target.value)} placeholder="Additional notes about the lead..." rows={2} data-testid="add-lead-notes" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={saving} className="bg-teal-600 hover:bg-teal-700" data-testid="submit-lead-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Add Lead
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* ─── Follow-up Dialog ─── */
export const FollowUpDialog = ({ open, onOpenChange, lead, onSuccess }) => {
  const [form, setForm] = useState({
    follow_up_date: '', follow_up_time: '', notes: '', follow_up_type: 'call'
  });

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async () => {
    if (!form.follow_up_date || !form.notes) {
      toast.error('Date and notes are required');
      return;
    }
    try {
      await leadsAPI.addFollowUp(lead?.id, form);
      toast.success('Follow-up scheduled');
      onOpenChange(false);
      setForm({ follow_up_date: '', follow_up_time: '', notes: '', follow_up_type: 'call' });
      onSuccess?.();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to schedule follow-up');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-teal-600" />
            Schedule Follow-up
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Date *</Label>
              <Input type="date" value={form.follow_up_date} onChange={(e) => update('follow_up_date', e.target.value)} />
            </div>
            <div>
              <Label>Time</Label>
              <Input type="time" value={form.follow_up_time} onChange={(e) => update('follow_up_time', e.target.value)} />
            </div>
          </div>
          <div>
            <Label>Type</Label>
            <Select value={form.follow_up_type} onValueChange={(v) => update('follow_up_type', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="call">Phone Call</SelectItem>
                <SelectItem value="whatsapp">WhatsApp</SelectItem>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="meeting">Meeting</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Notes *</Label>
            <Textarea value={form.notes} onChange={(e) => update('notes', e.target.value)} placeholder="What to discuss..." rows={2} data-testid="followup-notes" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={handleSubmit} className="bg-teal-600 hover:bg-teal-700" data-testid="submit-followup-btn">Schedule</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* ─── Lead Detail Dialog ─── */
export const LeadDetailDialog = ({ open, onOpenChange, lead, onNoteAdded, getStatusBadge }) => {
  const [noteText, setNoteText] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [feeAmount, setFeeAmount] = useState('');
  const [savingFee, setSavingFee] = useState(false);

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    setAddingNote(true);
    try {
      await leadsAPI.addNote(lead?.id, noteText.trim());
      toast.success('Note added');
      setNoteText('');
      onNoteAdded?.();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to add note');
    } finally {
      setAddingNote(false);
    }
  };

  const handleSaveFee = async () => {
    const amt = parseFloat(feeAmount);
    if (Number.isNaN(amt) || amt < 0) {
      toast.error('Enter a valid amount');
      return;
    }
    setSavingFee(true);
    try {
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${backendUrl}/api/leads/${lead.id}/consultancy-fee`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: amt }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to save');
      toast.success(`Consultancy fee of PKR ${amt.toLocaleString()} recorded — auto-syncs to P&L calculator`);
      setFeeAmount('');
      onNoteAdded?.(); // triggers parent refresh
    } catch (err) {
      toast.error(err.message || 'Failed to save fee');
    } finally {
      setSavingFee(false);
    }
  };

  const handleClearFee = async () => {
    if (!window.confirm('Clear the consultancy fee for this lead?')) return;
    setSavingFee(true);
    try {
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${backendUrl}/api/leads/${lead.id}/consultancy-fee`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ clear: true }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to clear');
      toast.success('Consultancy fee cleared');
      onNoteAdded?.();
    } catch (err) {
      toast.error(err.message || 'Failed to clear fee');
    } finally {
      setSavingFee(false);
    }
  };

  if (!lead) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="w-5 h-5 text-teal-600" />
            Lead Details
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><Label className="text-gray-500">Name</Label><p className="font-medium">{lead.full_name}</p></div>
            <div><Label className="text-gray-500">Phone</Label><p className="font-medium">{lead.phone}</p></div>
            <div><Label className="text-gray-500">Email</Label><p className="font-medium">{lead.email || '-'}</p></div>
            <div><Label className="text-gray-500">City</Label><p className="font-medium">{lead.city || '-'}</p></div>
            <div><Label className="text-gray-500">Country Interest</Label><p className="font-medium">{lead.country_interest || '-'}</p></div>
            <div><Label className="text-gray-500">Source</Label><p className="font-medium">{lead.source}</p></div>
            <div><Label className="text-gray-500">Status</Label>{getStatusBadge(lead.status)}</div>
          </div>

          {/* Consultancy Fee - feeds live into the P&L Calculator */}
          <div className="rounded-xl border-2 border-emerald-200 dark:border-emerald-800/50 bg-emerald-50/50 dark:bg-emerald-900/10 p-4">
            <div className="flex items-center gap-2 mb-3">
              <DollarSign className="w-4 h-4 text-emerald-700 dark:text-emerald-400" />
              <span className="font-semibold text-sm text-emerald-900 dark:text-emerald-200">Consultancy Fee</span>
              {lead.consultancy_fee_paid_amount ? (
                <Badge className="bg-emerald-600 text-white text-[10px] ml-1">
                  <CheckCircle2 className="w-3 h-3 mr-1" />PAID
                </Badge>
              ) : null}
            </div>
            {lead.consultancy_fee_paid_amount ? (
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <Label className="text-gray-500 text-xs">Amount Paid</Label>
                    <p className="font-bold text-lg text-emerald-700 dark:text-emerald-400">
                      PKR {Number(lead.consultancy_fee_paid_amount).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <Label className="text-gray-500 text-xs">Paid On</Label>
                    <p className="font-medium">
                      {lead.consultancy_fee_paid_at ? new Date(lead.consultancy_fee_paid_at).toLocaleDateString() : '-'}
                    </p>
                  </div>
                </div>
                {lead.consultancy_fee_paid_by_name && (
                  <p className="text-xs text-gray-500">Recorded by <strong>{lead.consultancy_fee_paid_by_name}</strong></p>
                )}
                <p className="text-[11px] text-emerald-700 dark:text-emerald-400 italic">
                  Auto-synced to the P&amp;L Calculator's current-month revenue.
                </p>
                <Button
                  onClick={handleClearFee}
                  disabled={savingFee}
                  variant="outline"
                  size="sm"
                  className="text-rose-600 border-rose-300 hover:bg-rose-50 h-7 text-xs"
                >
                  <Trash2 className="w-3 h-3 mr-1" /> Clear
                </Button>
              </div>
            ) : (
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <Label className="text-gray-500 text-xs">Amount (PKR)</Label>
                  <Input
                    type="number"
                    value={feeAmount}
                    onChange={(e) => setFeeAmount(e.target.value)}
                    placeholder="e.g. 25000"
                    className="h-9 mt-1"
                    data-testid="consultancy-fee-amount-input"
                  />
                </div>
                <Button
                  onClick={handleSaveFee}
                  disabled={savingFee || !feeAmount}
                  className="bg-emerald-600 hover:bg-emerald-700 h-9"
                  data-testid="save-consultancy-fee-btn"
                >
                  {savingFee ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-1" /> Mark Paid</>}
                </Button>
              </div>
            )}
          </div>

          {/* Notes Section */}
          <div className="space-y-3">
            <Label className="text-gray-500 flex items-center gap-2"><StickyNote className="w-4 h-4" />Notes</Label>
            <div className="bg-gray-50 dark:bg-slate-700 p-3 rounded-lg">
              <Label className="text-sm text-gray-600 dark:text-gray-300 mb-2 block">Add New Note</Label>
              <Textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Type a new note about this lead..." rows={2} className="text-sm mb-2" data-testid="detail-note-input" />
              <Button size="sm" className="bg-teal-600 hover:bg-teal-700" data-testid="add-note-btn" disabled={addingNote || !noteText.trim()} onClick={handleAddNote}>
                {addingNote ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                Add Note
              </Button>
            </div>
            <div>
              <Label className="text-sm text-gray-600 dark:text-gray-300 mb-2 block">Note History ({(lead.notes_history || []).length} notes)</Label>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {(lead.notes_history || []).length > 0 ? (
                  [...(lead.notes_history || [])].reverse().map((noteItem, idx) => (
                    <div key={idx} className="bg-white dark:bg-slate-600 p-3 rounded border text-sm">
                      <p className="text-gray-800 dark:text-white whitespace-pre-wrap">{noteItem.note}</p>
                      <p className="text-xs text-gray-400 mt-2">{noteItem.by} &bull; {new Date(noteItem.at).toLocaleString()}</p>
                    </div>
                  ))
                ) : lead.notes ? (
                  <div className="bg-white dark:bg-slate-600 p-3 rounded border text-sm">
                    <p className="text-gray-800 dark:text-white whitespace-pre-wrap">{lead.notes}</p>
                    <p className="text-xs text-gray-400 mt-2">Legacy note</p>
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 italic">No notes yet. Add one above.</p>
                )}
              </div>
            </div>
          </div>

          {/* Activity Log */}
          <div>
            <ActivityFeedTimeline entityType="lead" entityId={lead.id} maxHeight="18rem" />
          </div>

          {/* Legacy Activity Log (pre-feed entries) */}
          {(lead.activity_log || []).length > 0 && (
            <div>
              <Label className="text-gray-500 mb-2 block">Legacy Activity Log</Label>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {(lead.activity_log || []).slice().reverse().map((activity, idx) => (
                  <div key={idx} className="flex items-start gap-2 text-sm p-2 bg-gray-50 rounded">
                    <Clock className="w-4 h-4 text-gray-400 mt-0.5" />
                    <div>
                      <p className="font-medium">{activity.action}</p>
                      <p className="text-gray-500 text-xs">{activity.details}</p>
                      <p className="text-gray-400 text-xs">{activity.by} &bull; {new Date(activity.at).toLocaleString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Follow-ups */}
          {lead.follow_ups?.length > 0 && (
            <div>
              <Label className="text-gray-500 mb-2 block">Scheduled Follow-ups</Label>
              <div className="space-y-2">
                {lead.follow_ups.map((fu, idx) => (
                  <div key={idx} className={`flex items-center justify-between p-2 rounded ${fu.completed ? 'bg-green-50' : 'bg-yellow-50'}`}>
                    <div>
                      <p className="text-sm font-medium">{fu.follow_up_type} - {fu.follow_up_date}</p>
                      <p className="text-xs text-gray-500">{fu.notes}</p>
                    </div>
                    {fu.completed ? (
                      <Badge className="bg-green-100 text-green-700">Completed</Badge>
                    ) : (
                      <Badge className="bg-yellow-100 text-yellow-700">Pending</Badge>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
